import { useEffect, useRef, useState } from 'react'

const TRANSITION_DURATION = 300
const MEASURE_FRAME_LIMIT = 60

const requestNextFrame = (callback) => {
  if (typeof window !== 'undefined' && window.requestAnimationFrame) {
    return window.requestAnimationFrame(callback)
  }

  return setTimeout(callback, 0)
}

const cancelNextFrame = (frame) => {
  if (!frame) return
  if (typeof window !== 'undefined' && window.cancelAnimationFrame) {
    window.cancelAnimationFrame(frame)
    return
  }

  clearTimeout(frame)
}

const getHeight = (element) => (element ? element.scrollHeight : 0)

export const useShowHideTransition = (
  isShow,
  duration = TRANSITION_DURATION,
  isInitialTransition = false,
  transitionKey,
) => {
  const ref = useRef(null)
  const isFirstRender = useRef(true)
  const hasInitialTransition = isInitialTransition && isShow
  const [isRender, setIsRender] = useState(isShow)
  const [isActive, setIsActive] = useState(
    hasInitialTransition ? false : isShow,
  )
  const [height, setHeight] = useState(
    isShow ? (hasInitialTransition ? 0 : 'auto') : 0,
  )

  useEffect(() => {
    if (isFirstRender.current && !hasInitialTransition) {
      isFirstRender.current = false
      return
    }
    isFirstRender.current = false

    let frame
    let timer

    if (isShow) {
      let measureFrame = 0

      setIsRender(true)
      setIsActive(false)
      setHeight(0)

      const updateShowHeight = () => {
        let nextHeight = getHeight(ref.current)

        if (!nextHeight && measureFrame < MEASURE_FRAME_LIMIT) {
          measureFrame += 1
          frame = requestNextFrame(updateShowHeight)
          return
        }

        setHeight(nextHeight || 'auto')
        setIsActive(true)

        timer = setTimeout(() => {
          setHeight('auto')
        }, duration)
      }

      frame = requestNextFrame(updateShowHeight)
    } else if (ref.current) {
      setHeight(getHeight(ref.current))
      setIsActive(true)

      frame = requestNextFrame(() => {
        setHeight(0)
        setIsActive(false)

        timer = setTimeout(() => {
          setIsRender(false)
        }, duration)
      })
    } else {
      setIsRender(false)
      setIsActive(false)
      setHeight(0)
    }

    return () => {
      cancelNextFrame(frame)
      clearTimeout(timer)
    }
  }, [isShow, duration, hasInitialTransition, transitionKey])

  return {
    ref,
    hidden: !isRender,
    style: {
      height,
      opacity: isActive ? 1 : 0,
      overflow: height === 'auto' ? undefined : 'hidden',
      pointerEvents: isActive ? undefined : 'none',
      transition: `height ${duration}ms ease, opacity ${duration}ms ease`,
    },
  }
}
